import { useProvidePageBottomOffset } from '@/contexts/page-bottom-offset-context'
import {
  createContext,
  type CSSProperties,
  type ReactNode,
  type Ref,
  useCallback,
  useContext,
  useMemo,
} from 'react'
import {
  type ScreenHeaderBorderProps,
  ScreenHeaderProvider,
  ScreenHeaderContent,
  ScreenHeaderMain,
  ScreenHeaderPersistentTopBar,
  ScreenHeaderTitle,
  ScreenHeaderPersistentTopBarLayout,
  ScreenHeaderPersistentTopBarTitle,
  useTopBarElementScrollProgress,
  ScreenHeaderBorder,
  useScreenHeaderTotalOffset,
  type ScreenHeaderPersistentTopBarTitleProps,
} from '@/components/ScreenHeader'
import { usePageTopOffset } from '@/contexts/page-top-offset-context'
import { ProgressBar } from '@/components/ProgressBar'
import { useIsSpacious } from '@/hooks/viewport-hooks'
import { AnimatePresence, LazyMotion, m, type Variants } from 'motion/react'
import { useElementSize } from '@/hooks/element-hooks'
import styles from './Wizard.module.css'
import { RawLink } from '@/components/Link'
import { AutoSkeleton, useIsSkeleton } from '@/components/Skeleton'
import cx from 'classnames'
import { Button, type ButtonProps } from '@/components/Button'
import { useCallbackRef } from '@/hooks/ref-hooks'
import { useComposedRefs } from '@radix-ui/react-compose-refs'
import { useObservableValue, useSubject } from '@/hooks/rx-hooks'
import { BehaviorSubject, delay, of, switchMap } from 'rxjs'
import { distinctUntilChanged, startWith } from 'rxjs/operators'
import { createLayeredContext } from '@/contexts/layered-context'
import { useProvideScrollPaddingBottom } from '@/contexts/scroll-padding-context'
import { useProvideSupportWidgetBottomOffset } from '@/features/support/components/ChatSupportSpaceManager'

const loadMotionFeatures = () =>
  import('@/infra/motion-features').then((r) => r.default)

export interface WizardProps {
  children?: ReactNode
  size?: 'default' | 'lg'
  topOffset?: number
  testId?: string
}

/**
 * Wizard screen root.
 * The following subcomponents MUST be rendered:
 * - {@link WizardTopBar}
 * - {@link WizardContentHeader}
 *    - {@link WizardContentHeaderTitle}
 */
export function Wizard({ children, size, topOffset = 0, testId }: WizardProps) {
  const pageTopOffset = usePageTopOffset()

  const {
    connectRef: connectBottomBarRef,
    size: { height: bottomBarHeight },
  } = useElementSize()

  const isIntersecting$ = useSubject(() => new BehaviorSubject(false))
  const isContentPrimaryButtonVisible$ = useMemo(
    () =>
      isIntersecting$.pipe(
        switchMap((intersecting) =>
          intersecting ? of(true) : of(false).pipe(delay(30)),
        ),
        distinctUntilChanged(),
        startWith(true),
      ),
    [isIntersecting$],
  )
  const isContentPrimaryButtonVisible = useObservableValue(
    isContentPrimaryButtonVisible$,
  )
  const connectContentPrimaryButtonIntersectionRef =
    useCallbackRef<HTMLDivElement>(
      useCallback(
        (element: HTMLDivElement) => {
          const intersectionObserver = new IntersectionObserver(
            (entries) => {
              isIntersecting$.next(entries[0].isIntersecting)
            },
            {
              threshold: 0.8,
            },
          )

          intersectionObserver.observe(element)
          return () => intersectionObserver.disconnect()
        },
        [isIntersecting$],
      ),
    )

  const contextValue = useMemo(
    (): InternalWizardContextValue => ({
      bottomBarHeight,
      bottomBarRef: connectBottomBarRef,
      isContentPrimaryButtonVisible,
      connectContentPrimaryButtonIntersectionRef,
    }),
    [
      bottomBarHeight,
      connectBottomBarRef,
      isContentPrimaryButtonVisible,
      connectContentPrimaryButtonIntersectionRef,
    ],
  )

  useProvidePageBottomOffset(bottomBarHeight)
  useProvideScrollPaddingBottom(bottomBarHeight)

  return (
    <LazyMotion features={loadMotionFeatures}>
      <div
        data-test={testId}
        className={cx(styles.root, size === 'lg' && styles.lg)}
        style={{ '--page-top-offset': `${pageTopOffset}px` } as CSSProperties}
      >
        <InternalWizardContext value={contextValue}>
          <ScreenHeaderProvider
            applyDocumentScrollPadding
            topOffset={pageTopOffset + topOffset}
          >
            {children}
          </ScreenHeaderProvider>
        </InternalWizardContext>
      </div>
    </LazyMotion>
  )
}

interface WizardTopBarProps {
  left?: ReactNode
  right?: ReactNode
  title?: string
  borderVisibility?: ScreenHeaderBorderProps['visibility'] | 'none'
  titleTextAnimationKey?: string
  titleVisibility?: ScreenHeaderPersistentTopBarTitleProps['visibility']
}

/**
 * Top bar for the wizard.
 */
export function WizardTopBar({
  left,
  right,
  title,
  borderVisibility,
  titleTextAnimationKey,
  titleVisibility = 'content-exit',
}: WizardTopBarProps) {
  const isSpacious = useIsSpacious()
  const titleScrollProgress = useTopBarElementScrollProgress('content')

  return (
    <ScreenHeaderPersistentTopBar>
      {isSpacious ? (
        <div className={styles.topBarLayout}>
          <div className={styles.topBar}>
            {left && <div className={styles.topBarLeft}>{left}</div>}
            <div className={styles.topBarContent}>
              <m.h1
                className={styles.topBarTitle}
                style={{ opacity: titleScrollProgress }}
              >
                {title}
              </m.h1>
            </div>
            {right && <div className={styles.topBarRight}>{right}</div>}
          </div>
        </div>
      ) : (
        <ScreenHeaderPersistentTopBarLayout
          visibility="always"
          left={left}
          right={right}
        >
          <ScreenHeaderPersistentTopBarTitle
            textAnimationKey={titleTextAnimationKey}
            visibility={titleVisibility}
          >
            {title}
          </ScreenHeaderPersistentTopBarTitle>
        </ScreenHeaderPersistentTopBarLayout>
      )}
      {borderVisibility !== 'none' && (
        <ScreenHeaderBorder visibility={borderVisibility} />
      )}
    </ScreenHeaderPersistentTopBar>
  )
}

interface WizardTopBarLogoProps {
  src: string
  alt?: string
  to?: string
  href?: string
  onPress?: () => unknown
  testId?: string
}

/**
 * Wizard Top Bar Logo
 */
export function WizardTopBarLogo({
  testId,
  src,
  alt,
  ...rest
}: WizardTopBarLogoProps) {
  return (
    <div className={styles.topBarLogoContainer}>
      <RawLink className={styles.topBarLogoLink} testId={testId} {...rest}>
        <img className={styles.topBarLogo} src={src} alt={alt} />
      </RawLink>
    </div>
  )
}

/**
 * Sentinel header that can be used in place of {@link WizardContentHeader}
 * if there's no clear header content for the wizard.
 *
 * Necessary due to how the screen header components work.
 */
export function WizardContentSentinelHeader() {
  const isSpacious = useIsSpacious()

  /*
    Due to an issue with Motion's scroll hook and presence animations,
    we have to do a complete swap when switching between layouts to make sure
    there's no point in which the element refs are null. I really don't
    know why it's happening, since we've been able to account for this
    in our own hooks.
  */
  return (
    <ScreenHeaderMain key={isSpacious ? 'spacious' : 'compact'}>
      <ScreenHeaderContent />
    </ScreenHeaderMain>
  )
}

interface WizardContentHeaderProps {
  children?: ReactNode
}

/**
 * Wizard Content Header
 */
export function WizardContentHeader({ children }: WizardContentHeaderProps) {
  const isSpacious = useIsSpacious()

  /*
    Due to an issue with Motion's scroll hook and presence animations,
    we have to do a complete swap when switching between layouts to make sure
    there's no point in which the element refs are null. I really don't
    know why it's happening, since we've been able to account for this
    in our own hooks.
  */
  return (
    <ScreenHeaderMain key={isSpacious ? 'spacious' : 'compact'}>
      <ScreenHeaderContent>
        <header className={styles.contentHeader}>{children}</header>
      </ScreenHeaderContent>
    </ScreenHeaderMain>
  )
}

interface WizardContentHeaderKickerProps {
  children?: ReactNode
  testId?: string
}

/**
 * Wizard Content Header Kicker
 */
export function WizardContentHeaderKicker({
  children,
  testId,
}: WizardContentHeaderKickerProps) {
  return (
    <p data-test={testId} className={styles.contentHeaderKicker}>
      {children}
    </p>
  )
}

interface WizardContentHeaderTitleProps {
  children?: ReactNode
  id?: string
  testId?: string
}

/**
 * Title for the wizard content.
 *
 * Used inside a {@link WizardContentHeader}.
 */
export function WizardContentHeaderTitle({
  children,
  id,
  testId,
}: WizardContentHeaderTitleProps) {
  return (
    <ScreenHeaderTitle>
      <h1 data-test={testId} id={id} className={styles.contentHeaderTitle}>
        {children}
      </h1>
    </ScreenHeaderTitle>
  )
}

interface WizardContentHeaderDescriptionProps {
  children?: ReactNode
  testId?: string
}

/**
 * Wizard Content Header Description
 */
export function WizardContentHeaderDescription({
  children,
  testId,
}: WizardContentHeaderDescriptionProps) {
  return (
    <div data-test={testId} className={styles.contentHeaderDescription}>
      {children}
    </div>
  )
}

interface WizardContentBodyProps {
  children?: ReactNode
}

/**
 * Wizard Content Body
 */
export function WizardContentBody({ children }: WizardContentBodyProps) {
  return <div className={styles.contentBody}>{children}</div>
}

interface WizardContentFooterProps {
  children?: ReactNode
}

/**
 * Wizard Content Footer
 */
export function WizardContentFooter({ children }: WizardContentFooterProps) {
  const { connectContentPrimaryButtonIntersectionRef } = useContext(
    InternalWizardContext,
  )

  return (
    <div
      ref={connectContentPrimaryButtonIntersectionRef}
      className={styles.contentFooter}
    >
      {children}
    </div>
  )
}

interface WizardContentPrimaryButtonProps extends ButtonProps {}

/**
 * Wizard Content Primary Button
 */
export function WizardContentPrimaryButton({
  ref,
  ...rest
}: WizardContentPrimaryButtonProps) {
  const composedRef = useComposedRefs(ref)
  return <Button ref={composedRef} {...rest} />
}

interface WizardPageSingleColumnLayoutProps {
  children?: ReactNode
}

/**
 * Wizard Page Single Column Layout
 */
export function WizardPageSingleColumnLayout({
  children,
}: WizardPageSingleColumnLayoutProps) {
  return <main className={styles.pageSingleColumnLayout}>{children}</main>
}

interface WizardPageSingleColumnRowProps {
  children?: ReactNode
  ref?: Ref<HTMLDivElement>
  sticky?: boolean
  /**
   * If specified, allows rendering a custom border beneath the content that
   * remains sticky.
   */
  border?: ReactNode
  /**
   * How much space to apply on the sides.
   */
  spacingX?: '3xl' | 'base'
}

/**
 * A row in the single column layout.
 */
export function WizardPageSingleColumnRow({
  children,
  ref,
  sticky,
  border,
  spacingX = '3xl',
}: WizardPageSingleColumnRowProps) {
  const offset = useScreenHeaderTotalOffset()
  return (
    // We need 3 layers here:
    // * Outer: sticky positioning, full width and no padding
    // * Middle: padding
    // * Inner: max width
    // This is so we can have the overall thing be sticky, which lets us use
    // a full-width border, but still apply padding based on the design.
    <div
      style={sticky ? { top: offset } : undefined}
      className={cx(
        styles.pageSingleColumnRowOuter,
        sticky && styles.pageSingleColumnRowSticky,
      )}
    >
      <div
        className={cx(
          styles.pageSingleColumnRow,
          spacingX === 'base' && styles.spacingXBase,
        )}
      >
        <div className={styles.pageSingleColumnRowInner} ref={ref}>
          {children}
        </div>
      </div>
      {border && (
        <div className={styles.pageSingleColumnRowBorder}>{border}</div>
      )}
    </div>
  )
}

interface WizardPageLayoutProps {
  children?: ReactNode
  testId?: string
}

/**
 * Wizard Page Layout
 */
export function WizardPageLayout({ children, testId }: WizardPageLayoutProps) {
  const { bottomBarHeight } = useContext(InternalWizardContext)
  const layoutHeight = `calc(100vh - ${bottomBarHeight}px)`
  return (
    <div className={styles.pageLayout} data-test={testId}>
      <div
        className={styles.pageLayoutInner}
        style={{ minHeight: layoutHeight }}
      >
        {children}
      </div>
    </div>
  )
}

interface WizardPageStickyColumnProps {
  children?: ReactNode
  stretch?: boolean
}

/**
 * Wizard Page Sticky Column
 */
export function WizardPageStickyColumn({
  children,
  stretch,
}: WizardPageStickyColumnProps) {
  const { bottomBarHeight } = useContext(InternalWizardContext)
  const pageTopOffset = useScreenHeaderTotalOffset()
  const columnHeight = `calc(100vh - ${pageTopOffset + bottomBarHeight}px)`

  return (
    <div
      className={cx(styles.pageStickyColumn, stretch && styles.stretch)}
      style={
        {
          '--top-offset': `${pageTopOffset}px`,
          height: columnHeight,
        } as CSSProperties
      }
    >
      {children}
    </div>
  )
}

interface WizardPageMainColumnProps {
  children?: ReactNode
}

/**
 * Wizard Page Main Column
 */
export function WizardPageMainColumn({ children }: WizardPageMainColumnProps) {
  return (
    <m.main layoutRoot className={styles.pageMainColumn}>
      {children}
    </m.main>
  )
}

interface WizardPageMainColumnContentProps {
  children?: ReactNode
  animationDirection?: 'forward' | 'back'
  animationKey: string
}

const translateAmount = 15
const pageAnimatedContentVariants: Variants = {
  initial: (
    direction: WizardPageMainColumnContentProps['animationDirection'],
  ) => ({
    opacity: 0,
    x: direction === 'forward' ? translateAmount : -translateAmount,
  }),

  visible: {
    opacity: 1,
    x: '0%',
  },
}

const pageAnimatedContentVariantsSpacious: Variants = {
  initial: (
    direction: WizardPageMainColumnContentProps['animationDirection'],
  ) => ({
    opacity: 0,
    y: direction === 'forward' ? translateAmount : -translateAmount,
  }),

  visible: {
    opacity: 1,
    y: '0%',
  },
}

/**
 * Animation for content.
 */
export function WizardPageAnimatedContent({
  children,
  animationDirection = 'forward',
  animationKey,
}: WizardPageMainColumnContentProps) {
  const isSpacious = useIsSpacious()
  return (
    <m.div
      key={animationKey}
      custom={animationDirection}
      initial="initial"
      animate="visible"
      transition={{
        type: 'spring',
        stiffness: 500,
        damping: 30,
        delay: 0.05,
      }}
      variants={
        isSpacious
          ? pageAnimatedContentVariantsSpacious
          : pageAnimatedContentVariants
      }
    >
      {children}
    </m.div>
  )
}

interface WizardBottomBarProps {
  children?: ReactNode
  progress: number
}

/**
 * Bottom bar of the wizard, with progress information.
 */
export function WizardBottomBar({ progress, children }: WizardBottomBarProps) {
  const { bottomBarRef: ref, bottomBarHeight } = useContext(
    InternalWizardContext,
  )

  useProvideSupportWidgetBottomOffset(bottomBarHeight)
  useProvideScrollPaddingBottom(bottomBarHeight)

  return (
    <>
      {/* Sentinel element to occupy spacing for the bottom bar. */}
      <m.div style={{ height: bottomBarHeight }} />
      <div className={styles.bottomBar} ref={ref}>
        <div className={styles.bottomBarProgressBar}>
          <ProgressBar aria-label="Progress" progress={progress} />
        </div>
        <div className={styles.bottomBarLayout}>
          <div className={styles.bottomBarContent}>{children}</div>
        </div>
      </div>
    </>
  )
}

interface WizardBottomBarSectionProps {
  children?: ReactNode
}

/**
 * Wizard Bottom Bar Section
 */
export function WizardBottomBarSection({
  children,
}: WizardBottomBarSectionProps) {
  return <div className={styles.bottomBarSection}>{children}</div>
}

/**
 * Wizard Bottom Bar Spacer
 */
export function WizardBottomBarSpacer() {
  return <div className="flex-auto" />
}

interface WizardBottomBarLabelProps {
  children?: ReactNode
  isSkeleton?: boolean
}

/**
 * Label to use in the bottom bar.
 */
export function WizardBottomBarLabel({
  children,
  isSkeleton,
}: WizardBottomBarLabelProps) {
  isSkeleton = useIsSkeleton(isSkeleton)
  return (
    <p className={styles.bottomBarLabel}>
      <AutoSkeleton isSkeleton={isSkeleton}>{children}</AutoSkeleton>
    </p>
  )
}

const MotionButton = m.create(Button)

interface WizardBottomBarPrimaryButtonProps extends ButtonProps {
  isAlwaysVisible?: boolean
}

/**
 * Similar to a regular Button, but only shows when the primary button
 * in the content is not visible, unless {@link isAlwaysVisible} is `true`.
 */
export function WizardBottomBarPrimaryButton({
  isAlwaysVisible,
  ...rest
}: WizardBottomBarPrimaryButtonProps) {
  const { isContentPrimaryButtonVisible } = useContext(InternalWizardContext)
  return (
    <AnimatePresence initial>
      {(!isContentPrimaryButtonVisible || isAlwaysVisible) && (
        <MotionButton
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          {...rest}
        />
      )}
    </AnimatePresence>
  )
}

interface InternalWizardContextValue {
  bottomBarRef: Ref<HTMLDivElement>
  bottomBarHeight: number
  isContentPrimaryButtonVisible: boolean
  connectContentPrimaryButtonIntersectionRef: Ref<HTMLDivElement>
}

/**
 * Context for passing along a ref to the bottom bar.
 */
export const InternalWizardContext = createContext<InternalWizardContextValue>({
  bottomBarHeight: 0,
  bottomBarRef: null,
  isContentPrimaryButtonVisible: false,
  connectContentPrimaryButtonIntersectionRef: null,
})

const {
  useProvide: useProvideWizardBottomBarPrimaryButton,
  useValue: useWizardBottomBarPrimaryButton,
  Provider: WizardBottomBarPrimaryButtonContextProvider,
} = createLayeredContext<ButtonProps | null>(null, {
  excludeNulls: true,
})

export {
  useProvideWizardBottomBarPrimaryButton,
  useWizardBottomBarPrimaryButton,
  WizardBottomBarPrimaryButtonContextProvider,
}
